import { proxy } from 'valtio';

import handleQueueAgentAdded from './events/queue/handleQueueAgentAdded';
import handleQueueCreated from './events/queue/handleQueueCreated';
import handleQueueDeleted from './events/queue/handleQueueDeleted';
import handleQueueSessionDelivered from './events/queue/handleQueueSessionDelivered';
import handleQueueSessionJoin from './events/queue/handleQueueSessionJoin';
import handleQueueSessionLeft from './events/queue/handleQueueSessionLeft';
import handleQueueAgentRemoved from './events/queue/handleQueueAgentRemoved';
import handleQueueStatus from './events/queue/handleQueueStatus';
import handleAgentAway from './events/agent/handleAgentAway';
import handleAgentSessionDelivered from './events/agent/handleAgentSessionDelivered';
import handleAgentSessionTerminated from './events/agent/handleAgentSessionTerminated';
import handleAgentSessionOffered from './events/agent/handleAgentSessionOffered';
import handleAgentReady from './events/agent/handleAgentReady';
import handleAgentSessionRejected from './events/agent/handleAgentSessionRejected';
import handleAgentSessionRescinded from './events/agent/handleAgentSessionRescinded';
import handleAgentStatus from './events/agent/handleAgentStatus';
import handleAgentWrapupCompleted from './events/agent/handleAgentWrapupCompleted';
import handleAgentWrapupStarted from './events/agent/handleAgentWrapupStarted';
import handleSessionCreated from './events/session/handleSessionCreated';
import handleSessionDeleted from './events/session/handleSessionDeleted';
import getQueues from './getQueues';
import getUsers from './getUsers';
import ServiceQueue from './types/ServiceQueue';
import ServiceSession from './types/ServiceSession';
import ServiceUser from './types/ServiceUser';
import appManager from '@app/appManager';
import { RtcsClient, RtcsOptions } from '@servicetitan/telecom-rtcs-client';
import * as RTCS from '@servicetitan/telecom-rtcs-client';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import IAppLogger from '@app/lib/IAppLogger';
import environment from '@lib/environment';
import DiagnosticsModule from '@modules/DiagnosticsModule';

export type ServiceState = {
  version: number;
  loading: boolean;
  loadingStatus: string;
  error: unknown | null;
  id: string | null;
  contactCenterId: string | null;
  sessions: Record<string, ServiceSession>;
  queues: ServiceQueue[];
  users: ServiceUser[];
};

export const serviceState = proxy<ServiceState>({
  version: 0,
  loading: false,
  loadingStatus: '',
  error: null,
  id: null,
  contactCenterId: null,
  users: [],
  sessions: {},
  queues: [],
});

const workspaceUrl = environment.RTCS_HUB_URL;
let workspaceClient: RtcsClient | null = null;
let workspaceLogger: IAppLogger | null = null;
let workspaceTenantIds: string[] = [];
let workspaceAgentId: string | null = null;

export async function openWorkspace(
  userId: string,
  authToken: string,
  accountId: string,
  tenantIds: string[],
  logger: IAppLogger,
) {
  setLoadingState(true, 'Connecting...');
  serviceState.error = null;

  // make sure we're not already connected
  await closeWorkspace();

  // save workspace globals
  workspaceLogger = logger;
  workspaceTenantIds = tenantIds;

  logWorkspace('service:workspace:open', workspaceUrl, userId, tenantIds);

  // save user id
  serviceState.id = userId;

  // load our initial payloads
  try {
    const result = await Promise.all([getQueues(), getUsers()]);
    serviceState.queues = result[0];
    serviceState.users = result[1];
  } catch (err) {
    console.error('open workspace error', err);
    serviceState.error = err;
    setLoadingState(false);
    return;
  }

  // get contact center id
  serviceState.contactCenterId =
    serviceState.users.find((u) => u.id === userId)?.contactCenterUserId ??
    null;

  logWorkspace(
    'service:workspace:users-queues',
    serviceState.users,
    serviceState.queues,
  );

  try {
    const options: RtcsOptions = {
      url: workspaceUrl,
      authToken,
      accountId,
    };
    workspaceClient = await RTCS.connect(options);
    setupMessageListeners();

    // check our user status
    const user = serviceState.users.find((u) => u.id === userId);
    if (!user) {
      throw new Error('Your user account is not properly initialized');
    } else {
      workspaceAgentId = user.contactCenterUserId || null;
      notifyUserStatusChanged(user.status, user.canChangeStatus);
    }

    logWorkspace('service:workspace:setup-complete');
  } catch (err) {
    serviceState.error = err;
  } finally {
    setLoadingState(false);
  }
}

export async function closeWorkspace() {
  if (workspaceClient) {
    logWorkspace('service:workspace:close');
    workspaceClient.close();
    workspaceClient = null;
    workspaceLogger = null;
    workspaceAgentId = null;
    workspaceTenantIds = [];
    serviceState.id = null;
    serviceState.users = [];
    serviceState.sessions = {};
    serviceState.queues = [];
    serviceState.error = null;
    logWorkspace('service:workspace:closed');
  }
}

export function setLoadingState(loading: boolean, status = '') {
  serviceState.loading = loading;
  serviceState.loadingStatus = status;
}

async function setupMessageListeners() {
  if (workspaceClient) {
    workspaceClient.events.on('agent.*', async (e: RTCS.AgentEvent) => {
      if (e.type === 'agent.status') {
        return handleAgentStatus(e);
      } else if (e.type === 'agent.away') {
        return handleAgentAway(e);
      } else if (e.type === 'agent.ready') {
        return handleAgentReady(e);
      } else if (e.type === 'agent.sessionOffered') {
        return handleAgentSessionOffered(e);
      } else if (e.type === 'agent.sessionRescinded') {
        return handleAgentSessionRescinded(e);
      } else if (e.type === 'agent.sessionRejected') {
        return handleAgentSessionRejected(e);
      } else if (e.type === 'agent.sessionDelivered') {
        return handleAgentSessionDelivered(e);
      } else if (e.type === 'agent.sessionTerminated') {
        return handleAgentSessionTerminated(e);
      } else if (e.type === 'agent.wrapupStarted') {
        return handleAgentWrapupStarted(e);
      } else if (e.type === 'agent.wrapupCompleted') {
        return handleAgentWrapupCompleted(e);
      }
    });
    workspaceClient.events.on('session.*', async (e: RTCS.SessionEvent) => {
      if (e.type === 'session.created') {
        return handleSessionCreated(e);
      } else if (e.type === 'session.deleted') {
        return handleSessionDeleted(e);
      }
    });
    workspaceClient.events.on('queue.*', async (e: RTCS.QueueEvent) => {
      if (e.type === 'queue.sessionJoined') {
        return handleQueueSessionJoin(e);
      } else if (e.type === 'queue.status') {
        return handleQueueStatus(e);
      } else if (e.type === 'queue.sessionLeft') {
        return handleQueueSessionLeft(e);
      } else if (e.type === 'queue.sessionDelivered') {
        return handleQueueSessionDelivered(e);
      } else if (e.type === 'queue.created') {
        return handleQueueCreated(e);
      } else if (e.type === 'queue.deleted') {
        return handleQueueDeleted(e);
      } else if (e.type === 'queue.agentAdded') {
        return handleQueueAgentAdded(e);
      } else if (e.type === 'queue.agentRemoved') {
        return handleQueueAgentRemoved(e);
      }
    });
  }
}

export function getQueue(queueId: string): ServiceQueue | null {
  return serviceState.queues.find((q) => q.id === queueId) || null;
}

export function getUser(userId: string): ServiceUser | null {
  return (
    serviceState.users.find((u) => u.contactCenterUserId === userId) || null
  );
}

export function getSession(sessionId: string): ServiceSession | null {
  return serviceState.sessions[sessionId] || null;
}

export type ServiceUserStatusEvent = {
  status: UserSetAvailabilityStatus;
  canChange: boolean;
};

function notifyUserStatusChanged(
  status: UserSetAvailabilityStatus,
  canChange = true,
) {
  appManager.events.emit<ServiceUserStatusEvent>('user.status.updated', {
    status,
    canChange,
  });
}

function notifyUserStatusForceAway() {
  appManager.events.emit('user.status.force_away');
}

export function updateUserStatus(
  userId: string,
  status?: UserSetAvailabilityStatus,
  canChange?: boolean,
) {
  const user = serviceState.users.find((u) => u.id === userId);
  if (!user) {
    return;
  }

  const newStatus = status ?? user.status;
  const newCanChange = canChange ?? user.canChangeStatus;

  if (user.status !== newStatus || user.canChangeStatus !== newCanChange) {
    user.status = newStatus;
    user.canChangeStatus = newCanChange;

    // if this is the current user, notify app
    if (user.id === serviceState.id) {
      notifyUserStatusChanged(newStatus, newCanChange);
    }
  }
}

/**
 * Set the user status to unavailable and make it changeable. This is used when the user status is changed
 * to force_away by Kazoo upon a missed call. We don't have this status in our business logic, but this is
 * just a Unavailable status for us. Though we still need to set the status to unavailable so that we sync
 * FE and Kazoo statuses.
 * @param userId
 */
export function setUserUnavailable(userId: string) {
  const user = serviceState.users.find((u) => u.id === userId);
  if (!user) {
    return;
  }

  // if this is the current user, notify the app: one for setting the status and one for the making api call
  if (user.id === serviceState.id) {
    notifyUserStatusChanged(UserSetAvailabilityStatus.Unavailable, true); // set the status to unavailable and make it changeable
    notifyUserStatusForceAway(); // make the api call to set the status to unavailable so that we sync FE and Kazoo statuses
  }

  user.status = UserSetAvailabilityStatus.Unavailable;
}

export function logWorkspace(message: any, ...optionalParams: any[]): void {
  if (workspaceLogger) {
    workspaceLogger.info(message, optionalParams);
  }
}

export function applyMockedState<K extends keyof ServiceState>(
  prop: K,
  value: ServiceState[K],
) {
  serviceState[prop] = value;
}

export function getTenantId(tenantId: number, agentId?: string): string {
  // make sure the tenant id is valid if it's for the current user
  if (
    agentId &&
    agentId === workspaceAgentId &&
    !workspaceTenantIds.find((id) => id === tenantId.toString())
  ) {
    DiagnosticsModule.reportBackgroundError(
      `A call from a queue did not have a valid account id: ${tenantId}, try refreshing your browser.`,
      {
        priority: 'direct',
        extra: { tenantId },
      },
    );
    throw new Error();
  }
  return tenantId.toString();
}

export async function requireUser(userId: string): Promise<ServiceUser> {
  let user = getUser(userId);
  if (!user) {
    serviceState.users = await getUsers();
    user = getUser(userId);
  }
  if (!user) {
    DiagnosticsModule.reportBackgroundError(
      `A call from a queue did not find a valid user account: ${userId}, try refreshing your browser.`,
      {
        priority: 'direct',
        extra: { userId },
      },
    );
    throw new Error();
  }
  return user;
}
